import React, { useState, useContext, useEffect, useCallback } from "react";
import AdminemailLogsContext from "../../context/adminemailLogs/adminemailLogsContext";
import UserContext from "../../context/users/userContext";
import AuthContext from "../../context/auth/authContext";
import * as FaIcons from "react-icons/fa";

const FilterSidebar = ({ isOpen, onClose }) => {
  const adminemailLogsContext = useContext(AdminemailLogsContext);
  const userContext = useContext(UserContext);
  const authContext = useContext(AuthContext);

  const { user: authUser } = authContext;
  const { currentUser, user, charters, getCharters } = userContext;
  const {
    entries,
    getEntries,
    setCurrentPage,
    searchText,
    loading,
    pageSize,
    appliedFilters,
  } = adminemailLogsContext;

  const [isSearchLoading, setIsSearchLoading] = useState(false);

  const today = new Date();
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(today.getMonth() - 1);

  const formatDate = (date) => date.toISOString().slice(0, 10);
  const maxDate = formatDate(today);

  // Store the last applied filters to revert when closing without applying
  const [lastAppliedFilters, setLastAppliedFilters] = useState({
    charterName: "",
    learningCenterName: "",
    status: "",
    emailType: "",
    searchText: "",
    startDate: formatDate(oneMonthAgo),
    endDate: formatDate(today),
  });

  const [filters, setFilters] = useState({
    charterName: "",
    learningCenterName: "",
    status: "",
    emailType: "",
    searchText: "",
    startDate: formatDate(oneMonthAgo),
    endDate: formatDate(today),
  });

  const [dateValidationError, setDateValidationError] = useState("");
  const [availableCharters, setAvailableCharters] = useState([]);
  const [learningCenters, setLearningCenters] = useState([]);
  const [filteredLearningCenters, setFilteredLearningCenters] = useState([]);

  // Get active user
  const activeUser = authUser || currentUser || user;
  const isSuperAdmin = activeUser?.userStatus === "Super Admin";

  // Date validation function
  const validateDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) {
      setDateValidationError("");
      return true;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    const todayDate = new Date();

    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    todayDate.setHours(0, 0, 0, 0);

    if (start > todayDate) {
      setDateValidationError("Start date cannot be in the future");
      return false;
    }

    if (end > todayDate) {
      setDateValidationError("End date cannot be in the future");
      return false;
    }

    if (start > end) {
      setDateValidationError("Start date must be less than or equal to end date");
      return false;
    }

    setDateValidationError("");
    return true;
  };

  const fetchFilteredDataFromBackend = async () => {
    try {
      if (!validateDateRange(filters.startDate, filters.endDate)) {
        return;
      }

      setIsSearchLoading(true);
      setCurrentPage(1);

      const backendFilters = {
        learningCenterCharter: filters.charterName,
        learningCenterName: filters.learningCenterName,
        status: filters.status,
        emailType: filters.emailType,
        startDate: filters.startDate,
        endDate: filters.endDate,
        searchText: filters.searchText,
        page: 1,
        pageSize: pageSize || 300,
      };

      await getEntries(backendFilters);
      // Update lastAppliedFilters after successful application
      setLastAppliedFilters({...filters});
      onClose(); // Close sidebar after applying filters
    } catch (error) {
      console.error("Error fetching filtered data from backend:", error);
    } finally {
      setIsSearchLoading(false);
    }
  };

  // Get learning centers for user
  const getLearningCentersForUser = useCallback(() => {
    let centers = [];

    if (isSuperAdmin) {
      if (adminemailLogsContext.searchSites) {
        const allSites = adminemailLogsContext.searchSites.map((site, index) => ({
          id: site._id || site.id || `site-${index}`,
          name: site.name || `Site ${index + 1}`,
          charter: site.charter || "",
          address: site.address
            ? `${site.address.address_1 || ""} ${site.address.city || ""} ${
                site.address.state || ""
              }`.trim()
            : "",
        }));
        centers = [...centers, ...allSites];
      }

      // if (charters) {
      //   const allCharterCenters = charters.map((charter, index) => ({
      //     id: charter._id || charter.id || `charter-${index}`,
      //     name: charter.name || `Charter ${index + 1}`,
      //     charter: charter.name || "",
      //     address: charter.address || charter.location || "",
      //   }));
      //   centers = [...centers, ...allCharterCenters];
      // }
    } else {
      if (
        activeUser?.charterAssignments &&
        activeUser.charterAssignments.length > 0
      ) {
        const assignedCharterNames = activeUser.charterAssignments.map(
          (assignment) => assignment.name || assignment.charter
        );

        if (adminemailLogsContext.searchSites) {
          const filteredSites = adminemailLogsContext.searchSites.filter((site) =>
            assignedCharterNames.includes(site.charter)
          );
          centers = filteredSites.map((site, index) => ({
            id: site._id || site.id || `site-${index}`,
            name: site.name || `Site ${index + 1}`,
            charter: site.charter || "",
            address: site.address
              ? `${site.address.address_1 || ""} ${site.address.city || ""} ${
                  site.address.state || ""
                }`.trim()
              : "",
          }));
        }

        if (userContext.charters) {
          const assignedCharterIds = activeUser.charterAssignments.map(
            (assignment) => assignment._id
          );
          const filteredCharters = userContext.charters.filter((charter) =>
            assignedCharterIds.includes(charter._id)
          );
          const charterCenters = filteredCharters.map((charter, index) => ({
            id: charter._id || charter.id || `charter-${index}`,
            name: charter.name || `Charter ${index + 1}`,
            charter: charter.name || "",
            address: charter.address || charter.location || "",
          }));
          centers = [...centers, ...charterCenters];
        }
      }
    }

    const uniqueCenters = centers.filter(
      (center, index, self) =>
        index === self.findIndex((c) => c.name === center.name)
    );

    return uniqueCenters;
  }, [authUser, currentUser, user, adminemailLogsContext.searchSites, userContext.charters]);

  // Get all available charters
  const getAvailableCharters = useCallback(() => {
    let charterList = [];

    if (entries && entries.length > 0) {
      const entryCharters = entries
        .map(
          (entry) =>
            entry.learningCenterCharter || entry.charter || entry.charterName
        )
        .filter(Boolean);

      charterList = [...new Set(entryCharters)];
    }

    if (isSuperAdmin) {
      if (adminemailLogsContext.searchSites) {
        const allCharters = [
          ...new Set(
            adminemailLogsContext.searchSites
              .map((site) => site.charter)
              .filter(Boolean)
          ),
        ];
        charterList = [...new Set([...charterList, ...allCharters])];
      }

      if (userContext.charters) {
        const additionalCharters = userContext.charters
          .map((charter) => charter.name)
          .filter(Boolean);
        charterList = [...new Set([...charterList, ...additionalCharters])];
      }
    }

    return [...new Set(charterList)].sort();
  }, [entries, activeUser, isSuperAdmin, adminemailLogsContext.searchSites, userContext.charters]);

  // Initialize data when component mounts
  useEffect(() => {
    setLearningCenters(getLearningCentersForUser());
    setAvailableCharters(getAvailableCharters());
  }, [getLearningCentersForUser, getAvailableCharters]);

  // Initialize filtered learning centers
  useEffect(() => {
    setFilteredLearningCenters(learningCenters);
  }, [learningCenters]);

  // Load data when component mounts
  useEffect(() => {
    if (userContext.getCharters) {
      getCharters();
    }

    if (adminemailLogsContext.getSearchSites) {
      adminemailLogsContext.getSearchSites();
    }
  }, []);

  // Initialize filters from appliedFilters when sidebar opens
  useEffect(() => {
    if (isOpen && appliedFilters) {
      const initialFilters = {
        charterName: appliedFilters.learningCenterCharter || "",
        learningCenterName: appliedFilters.learningCenterName || "",
        status: appliedFilters.status || "",
        emailType: appliedFilters.emailType || "",
        searchText: appliedFilters.searchText || "",
        startDate: appliedFilters.startDate || formatDate(oneMonthAgo),
        endDate: appliedFilters.endDate || formatDate(today),
      };
      setFilters(initialFilters);
      setLastAppliedFilters(initialFilters);
      setDateValidationError("");
    }
  }, [isOpen, appliedFilters]);

  // Filter learning centers based on selected charter
  useEffect(() => {
    if (filters.charterName) {
      const filtered = learningCenters.filter(
        (center) => center.charter === filters.charterName
      );
      setFilteredLearningCenters(filtered);

      if (
        filters.learningCenterName &&
        !filtered.find((center) => center.name === filters.learningCenterName)
      ) {
        setFilters((prev) => ({ ...prev, learningCenterName: "" }));
      }
    } else {
      setFilteredLearningCenters(learningCenters);
      if (filters.learningCenterName) {
        setFilters((prev) => ({ ...prev, learningCenterName: "" }));
      }
    }
  }, [filters.charterName, learningCenters, filters.learningCenterName]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => {
      const newFilters = {
        ...prev,
        [field]: value,
      };

      if (field === "startDate" || field === "endDate") {
        validateDateRange(
          field === "startDate" ? value : prev.startDate,
          field === "endDate" ? value : prev.endDate
        );
      }

      return newFilters;
    });
  };

  const clearAllFilters = () => {
    setFilters({
      charterName: "",
      learningCenterName: "",
      status: "",
      emailType: "",
      searchText: "",
      startDate: "",
      endDate: "",
    });
    setDateValidationError("");
  };

  // Handle closing the sidebar - revert to last applied filters if not applied
  const handleClose = () => {
    // Revert filters to the last applied state
    setFilters({...lastAppliedFilters});
    setDateValidationError("");
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (
        (filters.charterName ||
          filters.learningCenterName ||
          filters.status ||
          filters.startDate ||
          filters.endDate) &&
        !dateValidationError
      ) {
        fetchFilteredDataFromBackend();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div className="filter-sidebar-overlay" onClick={handleClose}></div>

      {/* Sidebar */}
      <div className={`filter-sidebar ${isOpen ? "open" : ""}`}>
        <div className="filter-sidebar-header">
          <h3>Filters</h3>
          <button className="close-btn" onClick={handleClose}>
            <FaIcons.FaTimes />
          </button>
        </div>

        <div className="filter-sidebar-content">
          {/* Charter Filter */}
          <div className="filter-section">
            <label className="filter-label">Charter</label>
            <select
              value={filters.charterName}
              onChange={(e) => handleFilterChange("charterName", e.target.value)}
              onKeyDown={handleKeyDown}
              className="filter-select"
            >
              <option value="">All Charters</option>
              {availableCharters.map((charter, index) => (
                <option key={index} value={charter}>
                  {charter}
                </option>
              ))}
            </select>
          </div>

          {/* Learning Center Filter */}
          <div className="filter-section">
            <label className="filter-label">Learning Center</label>
            <select
              value={filters.learningCenterName}
              onChange={(e) =>
                handleFilterChange("learningCenterName", e.target.value)
              }
              onKeyDown={handleKeyDown}
              className="filter-select"
              disabled={!filters.charterName}
            >
              <option value="">All Learning Centers</option>
              {filteredLearningCenters.map((center, index) => (
                <option key={center.id || index} value={center.name} title={center.name}>
                  {center.name}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div className="filter-section">
            <label className="filter-label">Status</label>
            <select
              value={filters.status || ""}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              onKeyDown={handleKeyDown}
              className="filter-select"
            >
              <option value="">All Status</option>
              <option value="Sent">Sent</option>
              <option value="Failed">Failed</option>
              <option value="Pending">Pending</option>
            </select>
          </div>

          {/* Email Type Filter */}
          <div className="filter-section">
            <label className="filter-label">Email Type</label>
            <select
              value={filters.emailType || ""}
              onChange={(e) => handleFilterChange("emailType", e.target.value)}
              onKeyDown={handleKeyDown}
              className="filter-select"
            >
              <option value="">All Email Types</option>
              <option value="Interest">Interest</option>
              <option value="Primary Enrollment">Primary Enrollment</option>
              <option value="Secondary Enrollment">Secondary Enrollment</option>
            </select>
          </div>

          {/* Date Range Filter */}
          <div className="filter-section">
            <label className="filter-label">Date Range</label>
            <div className="date-range-inputs">
              <div className="date-input-group">
                <label className="sub-label">Start Date</label>
                <input
                  type="date"
                  className="filter-input"
                  value={filters.startDate}
                  max={maxDate}
                  onChange={(e) => handleFilterChange("startDate", e.target.value)}
                />
              </div>
              <div className="date-input-group">
                <label className="sub-label">End Date</label>
                <input
                  type="date"
                  className="filter-input"
                  value={filters.endDate}
                  max={maxDate}
                  min={filters.startDate || undefined}
                  onChange={(e) => handleFilterChange("endDate", e.target.value)}
                />
              </div>
            </div>
            {dateValidationError && (
              <div className="error-message">
                <FaIcons.FaExclamationCircle />
                {dateValidationError}
              </div>
            )}
          </div>
        </div>

        <div className="filter-sidebar-footer">
          <button className="clear-all-btn" onClick={clearAllFilters}>
            Clear All
          </button>
          <button
            className="apply-filters-btn"
            onClick={fetchFilteredDataFromBackend}
            disabled={!!dateValidationError || isSearchLoading}
          >
            {isSearchLoading ? "Searching..." : "Apply Filters"}
          </button>
        </div>
      </div>
    </>
  );
};

export default FilterSidebar;
